/**
 * Calendar-date helpers. Dates are household-local `YYYY-MM-DD` strings and all
 * arithmetic happens in pure calendar space (UTC-based), so DST never shifts a date.
 * Only conversions between wall-clock time and epoch ms involve a timezone.
 */
import type { Weekday } from './types'

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

export function isDateString(s: unknown): s is string {
  if (typeof s !== 'string') return false
  const m = DATE_RE.exec(s)
  if (!m) return false
  const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!))
  return d.getUTCFullYear() === +m[1]! && d.getUTCMonth() === +m[2]! - 1 && d.getUTCDate() === +m[3]!
}

export function isTimeString(s: unknown): s is string {
  return typeof s === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(s)
}

export function parseDate(s: string): { y: number; m: number; d: number } {
  const m = DATE_RE.exec(s)
  if (!m) throw new Error(`Invalid date: ${s}`)
  return { y: +m[1]!, m: +m[2]!, d: +m[3]! }
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0')

export function formatDate(y: number, m: number, d: number): string {
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`
}

function toUTC(s: string): Date {
  const { y, m, d } = parseDate(s)
  return new Date(Date.UTC(y, m - 1, d))
}

function fromUTC(dt: Date): string {
  return formatDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate())
}

export function addDays(s: string, n: number): string {
  const dt = toUTC(s)
  dt.setUTCDate(dt.getUTCDate() + n)
  return fromUTC(dt)
}

export function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate()
}

/** Add months, clamping the day to the target month's end (Jan 31 + 1mo = Feb 28/29). */
export function addMonths(s: string, n: number, dayOfMonth?: number): string {
  const { y, m, d } = parseDate(s)
  const total = y * 12 + (m - 1) + n
  const ny = Math.floor(total / 12)
  const nm = (total % 12) + 1
  const want = dayOfMonth ?? d
  return formatDate(ny, nm, Math.min(want, daysInMonth(ny, nm)))
}

export function addYears(s: string, n: number): string {
  const { y, m, d } = parseDate(s)
  return formatDate(y + n, m, Math.min(d, daysInMonth(y + n, m)))
}

export function weekday(s: string): Weekday {
  return toUTC(s).getUTCDay() as Weekday
}

/** Whole days from a to b (b - a). */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUTC(b).getTime() - toUTC(a).getTime()) / 86400000)
}

export function compareDates(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** Next date with the given weekday; `strict` excludes `from` itself. */
export function nextWeekday(from: string, wd: Weekday, strict = true): string {
  const cur = weekday(from)
  let delta = (wd - cur + 7) % 7
  if (delta === 0 && strict) delta = 7
  return addDays(from, delta)
}

export function isWeekend(s: string): boolean {
  const w = weekday(s)
  return w === 0 || w === 6
}

/** Start of the week containing `s`. weekStart 0 = Sunday, 1 = Monday. */
export function startOfWeek(s: string, weekStart: 0 | 1 = 1): string {
  const w = weekday(s)
  const delta = (w - weekStart + 7) % 7
  return addDays(s, -delta)
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

export const weekdayShort = (s: string) => WEEKDAY_SHORT[weekday(s)]!
export const weekdayLong = (s: string) => WEEKDAY_LONG[weekday(s)]!
export const monthLong = (m: number) => MONTH_LONG[m - 1]!

/** "Tue Oct 7" */
export function formatDayShort(s: string): string {
  const { m, d } = parseDate(s)
  return `${weekdayShort(s)} ${MONTH_SHORT[m - 1]} ${d}`
}

/** "Oct 7" or "Oct 7, 2027" when not in `currentYear`. */
export function formatMonthDay(s: string, currentYear?: number): string {
  const { y, m, d } = parseDate(s)
  const base = `${MONTH_SHORT[m - 1]} ${d}`
  return currentYear !== undefined && y !== currentYear ? `${base}, ${y}` : base
}

/** Human label relative to today: Today, Tomorrow, Yesterday, Sat, Sat Oct 11, Oct 11. */
export function relativeDayLabel(s: string, today: string): string {
  const diff = daysBetween(today, s)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  if (diff === -1) return 'Yesterday'
  if (diff > 1 && diff < 7) return weekdayShort(s)
  const { y } = parseDate(today)
  if (diff >= 7 && diff < 14) return formatDayShort(s)
  return formatMonthDay(s, y)
}

/** "7:30 PM" from "19:30" */
export function formatTime(t: string): string {
  const [hh, mm] = t.split(':').map(Number) as [number, number]
  const h12 = hh % 12 === 0 ? 12 : hh % 12
  const ap = hh < 12 ? 'AM' : 'PM'
  return mm === 0 ? `${h12} ${ap}` : `${h12}:${pad(mm)} ${ap}`
}

/** A time in a mono label's few characters: "9:40A", "2P". */
export function formatTimeShort(t: string): string {
  return formatTime(t).replace(' AM', 'A').replace(' PM', 'P')
}

/**
 * A time as someone types it ("930", "9:30p", "9 pm", "21:15", "noon", "midnight") as "HH:MM",
 * or null. Without AM or PM, 1 to 11 take `half` (what the picker is on); 13 and up are 24-hour.
 */
export function parseClockText(s: string, half: 'am' | 'pm' = 'am'): string | null {
  const t = s.trim().toLowerCase().replace(/[\s.]/g, '')
  if (t === 'noon' || t === 'midday') return '12:00'
  if (t === 'midnight') return '00:00'
  const m = t.match(/^(\d{1,2})(?::?(\d{2}))?(a|am|p|pm)?$/)
  if (!m) return null
  let h = Number(m[1])
  const min = m[2] ? Number(m[2]) : 0
  if (min > 59) return null
  if (m[3]) {
    if (h < 1 || h > 12) return null
    if (m[3].startsWith('p') && h !== 12) h += 12
    if (m[3].startsWith('a') && h === 12) h = 0
  } else if (h > 23) return null
  else if (half === 'pm' && h >= 1 && h <= 11) h += 12
  return `${pad(h)}:${pad(min)}`
}

// ---------------------------------------------------------------------------
// Timezone conversions

const dtfCache = new Map<string, Intl.DateTimeFormat>()
function dtf(tz: string): Intl.DateTimeFormat {
  let f = dtfCache.get(tz)
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    dtfCache.set(tz, f)
  }
  return f
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** Wall-clock parts of an instant in `tz`. */
export function zonedParts(ms: number, tz: string) {
  const parts = dtf(tz).formatToParts(new Date(ms))
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0)
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24, mi: get('minute'), s: get('second') }
}

/** Household-local date of an instant. */
export function dateInTz(ms: number, tz: string): string {
  const p = zonedParts(ms, tz)
  return formatDate(p.y, p.m, p.d)
}

export function timeInTz(ms: number, tz: string): string {
  const p = zonedParts(ms, tz)
  return `${pad(p.h)}:${pad(p.mi)}`
}

export function todayIn(tz: string, now: number = Date.now()): string {
  return dateInTz(now, tz)
}

function tzOffsetMs(ms: number, tz: string): number {
  const p = zonedParts(ms, tz)
  const asUTC = Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s)
  return asUTC - Math.floor(ms / 1000) * 1000
}

/**
 * Epoch ms for a wall-clock date+time in `tz`. Handles DST: a time that doesn't
 * exist (spring-forward gap) resolves to the instant just after the gap; an
 * ambiguous time (fall-back) resolves to the first occurrence.
 */
export function zonedTimeToEpoch(date: string, time: string, tz: string): number {
  const { y, m, d } = parseDate(date)
  const [hh, mm] = time.split(':').map(Number) as [number, number]
  const wall = Date.UTC(y, m - 1, d, hh, mm)
  // Candidate A uses the offset at `wall`; it is valid if its own offset agrees.
  const o1 = tzOffsetMs(wall, tz)
  const a = wall - o1
  const o2 = tzOffsetMs(a, tz)
  if (o1 === o2) return a
  // Offset changed across the guess: try the other offset.
  const b = wall - o2
  if (tzOffsetMs(b, tz) === o2) return b
  // Neither round-trips: the wall time falls in a DST gap. Use the later instant.
  return Math.max(a, b)
}

/**
 * A wall-clock date & time in one zone as the same moment's in another: home time as this phone
 * shows it, or what's typed on a phone as home time. The same zone gives it back unchanged.
 */
export function convertWallTime(date: string, time: string, from: string, to: string): { date: string; time: string } {
  if (from === to) return { date, time }
  const ms = zonedTimeToEpoch(date, time, from)
  return { date: dateInTz(ms, to), time: timeInTz(ms, to) }
}

/** "Honolulu", "Los Angeles": the place a time zone is named after. */
export function zonePlace(tz: string): string {
  return (tz.split('/').pop() ?? tz).replace(/_/g, ' ')
}

/** Minutes since midnight for "HH:MM". */
export function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number) as [number, number]
  return h * 60 + m
}

/** Is local time `t` within [start, end) (wrapping past midnight)? */
export function inQuietHours(t: string, start: string, end: string): boolean {
  const x = timeToMinutes(t)
  const s = timeToMinutes(start)
  const e = timeToMinutes(end)
  if (s === e) return false
  return s < e ? x >= s && x < e : x >= s || x < e
}

/**
 * "Next weekend" target (SPEC §7.2 / §9.1): base = next Sat/Sun/Mon strictly after today.
 * With the Saturday base, items originally on a Sunday land on the following Sunday.
 */
export function nextWeekendTarget(today: string, setting: 'sat' | 'sun' | 'mon', originalDate?: string): string {
  const wd: Weekday = setting === 'sat' ? 6 : setting === 'sun' ? 0 : 1
  const base = nextWeekday(today, wd, true)
  if (setting === 'sat' && originalDate && weekday(originalDate) === 0) return addDays(base, 1)
  return base
}

/** Parse loose day words used in search prefixes / quick add: today, tomorrow, sat, 2026-10-04, 10/4. */
export function parseDayWord(word: string, today: string): string | null {
  const w = word.trim().toLowerCase()
  if (!w) return null
  if (w === 'today' || w === 'tod') return today
  if (w === 'tomorrow' || w === 'tmr' || w === 'tom') return addDays(today, 1)
  if (w === 'yesterday') return addDays(today, -1)
  const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  const idx = days.findIndex((d) => w.startsWith(d))
  if (idx >= 0 && w.length >= 3) return nextWeekday(today, idx as Weekday, false)
  if (isDateString(w)) return w
  const md = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/.exec(w)
  if (md) {
    const { y: ty } = parseDate(today)
    let y = md[3] ? +md[3] : ty
    if (y < 100) y += 2000
    const cand = formatDate(y, +md[1]!, +md[2]!)
    if (!isDateString(cand)) return null
    if (!md[3] && cand < addDays(today, -60)) return formatDate(y + 1, +md[1]!, +md[2]!)
    return cand
  }
  return null
}
